import React, { useEffect, useMemo, useRef, useState } from 'react';

// Уникальный счётчик id для всех iframe на странице
let _autoId = 0;

/**
 * iframe с auto-height через postMessage.
 * Внутрь srcDoc вставляется обёртка со сбросом стилей, шрифтом Benzin
 * и ResizeObserver, который шлёт высоту в parent.
 *
 * props:
 *   html        — фрагмент HTML body (без <html>/<head>/<body>)
 *   fixedHeight — число (px) если задана фиксированная высота, иначе auto
 *   className   — внешний класс на iframe
 *   onReady     — колбэк, вызывается когда шрифты загружены и контент готов
 */
const AutoHeightIframe = ({ html, fixedHeight, className = '', onReady }) => {
  const idRef = useRef(++_autoId);
  const [autoHeight, setAutoHeight] = useState(80);
  const [ready, setReady] = useState(false);
  const readyCalledRef = useRef(false);

  // Слушаем postMessage от своего iframe
  useEffect(() => {
    const onMessage = (e) => {
      const d = e.data;
      if (!d || d.id !== idRef.current) return;
      if (d.type === 'onepager-height') {
        if (typeof d.h === 'number' && d.h > 0 && !fixedHeight) {
          setAutoHeight((prev) => (Math.abs(prev - d.h) > 1 ? d.h : prev));
        }
      }
      if (d.type === 'onepager-ready') {
        if (!readyCalledRef.current) {
          readyCalledRef.current = true;
          setReady(true);
          if (onReady) onReady();
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [fixedHeight, onReady]);

  const srcDoc = useMemo(() => {
    const fontUrl = `${window.location.origin}/fonts/Benzin-Regular.ttf`;
    const frameId = idRef.current;
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
@font-face {
  font-family: 'Benzin';
  src: url('${fontUrl}') format('truetype');
  font-weight: 400;
  font-display: block;
}
html, body {
  margin: 0;
  padding: 0;
  background: transparent;
  color: #fff;
  font-family: 'Benzin', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  opacity: 0;
  transition: opacity 0.4s ease;
}
html.ready, html.ready body { opacity: 1; }
*, *::before, *::after { box-sizing: border-box; }
::-webkit-scrollbar { display: none; }
</style>
</head>
<body>
${html || ''}
<script>
(function () {
  var FRAME_ID = ${frameId};
  var lastH = 0;
  function postHeight() {
    try {
      var h = Math.max(
        document.documentElement.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      );
      if (h && Math.abs(h - lastH) > 1) {
        lastH = h;
        parent.postMessage({ type: 'onepager-height', id: FRAME_ID, h: h }, '*');
      }
    } catch (e) {}
  }
  function postReady() {
    try { parent.postMessage({ type: 'onepager-ready', id: FRAME_ID }, '*'); } catch (e) {}
  }
  function reveal() {
    document.documentElement.classList.add('ready');
    postHeight();
    postReady();
  }

  // Ждём загрузки шрифта Benzin перед показом контента (избегаем FOUT)
  function awaitFonts() {
    if (document.fonts && document.fonts.ready) {
      // Hard timeout — не блокируем больше 1.2с даже если шрифт сетевой
      var timer = setTimeout(reveal, 1200);
      document.fonts.ready.then(function () {
        clearTimeout(timer);
        reveal();
      }, function () {
        clearTimeout(timer);
        reveal();
      });
    } else {
      // Старый браузер
      setTimeout(reveal, 200);
    }
  }

  if (typeof ResizeObserver !== 'undefined') {
    try { new ResizeObserver(postHeight).observe(document.documentElement); } catch (e) {}
  }
  window.addEventListener('resize', postHeight);
  // Periodic safeguard — async ресурсы (ECharts) могут менять высоту позже
  var ticks = 0;
  var iv = setInterval(function () {
    postHeight();
    if (++ticks > 12) clearInterval(iv);
  }, 500);

  if (document.readyState === 'complete') awaitFonts();
  else window.addEventListener('load', awaitFonts);
})();
</script>
</body>
</html>`;
  }, [html]);

  const height = fixedHeight || autoHeight;

  return (
    <iframe
      title="onepager-section"
      srcDoc={srcDoc}
      className={className}
      style={{
        width: '100%',
        height: `${height}px`,
        border: 0,
        display: 'block',
        opacity: ready ? 1 : 0,
        transition: 'opacity 0.4s ease, height 0.3s ease',
      }}
      data-testid="onepager-iframe"
    />
  );
};

export default AutoHeightIframe;

